import type { AppViewState } from "./app-view-state.ts";
import type { IglooBotApp } from "./app.ts";
import type { AgentsListResult } from "./types.ts";
import { refreshChat, loadConsoleSkills } from "./app-chat.ts";
import { makeSwitchSession } from "./app-render.helpers.ts";
import {
  startLogsPolling,
  stopLogsPolling,
  startDebugPolling,
  stopDebugPolling,
} from "./app-polling.ts";
import { scheduleChatScroll, scheduleLogsScroll } from "./app-scroll.ts";
import {
  loadAgentDefinitions,
  type AgentDefinitionsState,
} from "./controllers/agent-definitions.ts";
import { loadAgentIdentities, loadAgentIdentity } from "./controllers/agent-identity.ts";
import { loadAgentSkills } from "./controllers/agent-skills.ts";
import { loadAgents } from "./controllers/agents.ts";
import { loadChannels } from "./controllers/channels.ts";
import { loadConfig, loadConfigSchema } from "./controllers/config.ts";
import { loadCronJobs, loadCronStatus } from "./controllers/cron.ts";
import { loadDebug } from "./controllers/debug.ts";
import { loadDevices } from "./controllers/devices.ts";
import { loadExecApprovals } from "./controllers/exec-approvals.ts";
import {
  loadIgloobotStats,
  loadIgloobotTab,
  loadProjects,
  type IgloobotState,
} from "./controllers/igloobot.ts";
import { loadLogs } from "./controllers/logs.ts";
import { loadNodes } from "./controllers/nodes.ts";
import { loadPresence } from "./controllers/presence.ts";
import { loadSessions } from "./controllers/sessions.ts";
import { loadSkillsSettingsList } from "./controllers/skills-settings.ts";
import { loadSkills } from "./controllers/skills.ts";
import {
  canAccessTab,
  inferBasePathFromPathname,
  normalizeBasePath,
  normalizePath,
  pathForTab,
  tabFromPath,
  type Tab,
} from "./navigation.ts";
import { loadAuth, saveSettings, type UiSettings } from "./storage.ts";
import { startThemeTransition, type ThemeTransitionContext } from "./theme-transition.ts";
import { resolveTheme, type ResolvedTheme, type ThemeMode } from "./theme.ts";

type SettingsHost = {
  settings: UiSettings;
  password?: string;
  theme: ThemeMode;
  themeResolved: ResolvedTheme;
  applySessionKey: string;
  sessionKey: string;
  tab: Tab;
  connected: boolean;
  chatHasAutoScrolled: boolean;
  logsAtBottom: boolean;
  eventLog: unknown[];
  eventLogBuffer: unknown[];
  basePath: string;
  agentsList?: AgentsListResult | null;
  agentsSelectedId?: string | null;
  agentsPanel?: "overview" | "files" | "tools" | "skills" | "tasks" | "channels" | "cron";
  themeMedia: MediaQueryList | null;
  themeMediaHandler: ((event: MediaQueryListEvent) => void) | null;
  pendingGatewayUrl?: string | null;
  modelCatalog: Array<{ id: string; name: string; provider: string }>;
};

export function applySettings(host: SettingsHost, next: UiSettings) {
  const normalized = {
    ...next,
    lastActiveSessionKey: next.lastActiveSessionKey?.trim() || next.sessionKey.trim() || "main",
  };
  host.settings = normalized;
  saveSettings(normalized);
  if (next.theme !== host.theme) {
    host.theme = next.theme;
    applyResolvedTheme(host, resolveTheme(next.theme));
  }
  host.applySessionKey = host.settings.lastActiveSessionKey;
}

export function setLastActiveSessionKey(host: SettingsHost, next: string) {
  const trimmed = next.trim();
  if (!trimmed) {
    return;
  }
  if (host.settings.lastActiveSessionKey === trimmed) {
    return;
  }
  applySettings(host, { ...host.settings, lastActiveSessionKey: trimmed });
}

export function applySettingsFromUrl(host: SettingsHost) {
  if (!window.location.search && !window.location.hash) {
    return;
  }
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const hashParams = new URLSearchParams(url.hash.startsWith("#") ? url.hash.slice(1) : url.hash);

  const tokenRaw = params.get("token") ?? hashParams.get("token");
  const passwordRaw = params.get("password") ?? hashParams.get("password");
  const sessionRaw = params.get("session") ?? hashParams.get("session");
  const gatewayUrlRaw = params.get("gatewayUrl") ?? hashParams.get("gatewayUrl");
  let shouldCleanUrl = false;

  if (tokenRaw != null) {
    const token = tokenRaw.trim();
    if (token && token !== host.settings.token) {
      applySettings(host, { ...host.settings, token });
    }
    params.delete("token");
    hashParams.delete("token");
    shouldCleanUrl = true;
  }

  if (passwordRaw != null) {
    const password = passwordRaw.trim();
    if (password) {
      (host as { password: string }).password = password;
    }
    params.delete("password");
    hashParams.delete("password");
    shouldCleanUrl = true;
  }

  if (sessionRaw != null) {
    const session = sessionRaw.trim();
    if (session) {
      host.sessionKey = session;
      applySettings(host, {
        ...host.settings,
        sessionKey: session,
        lastActiveSessionKey: session,
      });
    }
  }

  if (gatewayUrlRaw != null) {
    const gatewayUrl = gatewayUrlRaw.trim();
    if (gatewayUrl && gatewayUrl !== host.settings.gatewayUrl) {
      host.pendingGatewayUrl = gatewayUrl;
    }
    params.delete("gatewayUrl");
    hashParams.delete("gatewayUrl");
    shouldCleanUrl = true;
  }

  if (!shouldCleanUrl) {
    return;
  }
  url.search = params.toString();
  const nextHash = hashParams.toString();
  url.hash = nextHash ? `#${nextHash}` : "";
  window.history.replaceState({}, "", url.toString());
}

export function setTab(host: SettingsHost, next: Tab) {
  // RBAC tab guard: redirect to chat if the user doesn't have access
  const auth = loadAuth();
  const role = auth.currentUser?.globalRole ?? null;
  if (role && !canAccessTab(next, role)) {
    if (host.tab !== "chat") {
      host.tab = "chat";
    }
    return;
  }
  if (host.tab !== next) {
    host.tab = next;
  }
  if (next === "chat") {
    host.chatHasAutoScrolled = false;
  }
  if (next === "logs") {
    startLogsPolling(host as unknown as Parameters<typeof startLogsPolling>[0]);
  } else {
    stopLogsPolling(host as unknown as Parameters<typeof stopLogsPolling>[0]);
  }
  if (next === "debug") {
    startDebugPolling(host as unknown as Parameters<typeof startDebugPolling>[0]);
  } else {
    stopDebugPolling(host as unknown as Parameters<typeof stopDebugPolling>[0]);
  }
  void refreshActiveTab(host);
  syncUrlWithTab(host, next, false);
}

export function setTheme(host: SettingsHost, next: ThemeMode, context?: ThemeTransitionContext) {
  const applyTheme = () => {
    host.theme = next;
    applySettings(host, { ...host.settings, theme: next });
    applyResolvedTheme(host, resolveTheme(next));
  };
  startThemeTransition({
    nextTheme: next,
    applyTheme,
    context,
    currentTheme: host.theme,
  });
}

export async function refreshActiveTab(host: SettingsHost) {
  if (host.tab === "overview") {
    await loadOverview(host);
  }
  if (host.tab === "channels") {
    await loadChannelsTab(host);
    // Also load per-agent channel configs for the config section
    const ccMod = await import("./controllers/channel-configs.ts");
    const { loadAgents } = await import("./controllers/agents.ts");
    void loadAgents(host as unknown as import("./controllers/agents.ts").AgentsState).then(() => {
      const ccState =
        host as unknown as import("./controllers/channel-configs.ts").ChannelConfigsState;
      if (!ccState.ccSelectedAgentId && ccState.agentsList?.agents?.length) {
        ccState.ccSelectedAgentId = ccState.agentsList.agents[0].id;
      }
      void ccMod.loadChannelConfig(ccState);
    });
    void ccMod.loadAllChannelConfigs(
      host as unknown as import("./controllers/channel-configs.ts").ChannelConfigsState,
    );
  }
  if (host.tab === "sessions") {
    await loadSessions(host as unknown as IglooBotApp);
  }
  if (host.tab === "cron") {
    await loadCron(host);
    // Also load project cron for merged view
    const { loadGlobalProjectCronJobs } = await import("./controllers/igloobot.ts");
    void loadGlobalProjectCronJobs(
      host as unknown as import("./controllers/igloobot.ts").IgloobotState,
    );
  }
  if (host.tab === "skill-config") {
    await loadSkillsSettingsList(host as unknown as IglooBotApp);
  }
  if (host.tab === "skills") {
    await loadSkills(host as unknown as IglooBotApp);
  }
  if (host.tab === "agents") {
    await loadAgents(host as unknown as IglooBotApp);
    await loadConfig(host as unknown as IglooBotApp);
    // Load dynamic agent definitions — await so sidebar renders with data
    await loadAgentDefinitions(host as unknown as AgentDefinitionsState);
    // Load model catalog so the model picker shows all available models — await to avoid empty dropdowns
    if (host.modelCatalog.length === 0) {
      const { loadModelsTab } = await import("./controllers/models.ts");
      await loadModelsTab(host as unknown as import("./controllers/models.ts").ModelsState);
    }
    // Batch-load identities for all agents: static + dynamic
    const staticIds = host.agentsList?.agents?.map((entry) => entry.id) ?? [];
    const agentDefsState = host as unknown as AgentDefinitionsState;
    const dynamicIds = (agentDefsState.agentDefs ?? [])
      .map((d: { agentId: string }) => d.agentId)
      .filter((id: string) => !staticIds.includes(id));
    const allAgentIds = [...staticIds, ...dynamicIds];
    if (allAgentIds.length > 0) {
      void loadAgentIdentities(host as unknown as IglooBotApp, allAgentIds);
    }
    const agentId =
      host.agentsSelectedId ?? host.agentsList?.defaultId ?? host.agentsList?.agents?.[0]?.id;
    if (agentId) {
      void loadAgentIdentity(host as unknown as IglooBotApp, agentId);
      if (host.agentsPanel === "skills") {
        void loadAgentSkills(host as unknown as IglooBotApp, agentId);
      }
      if (host.agentsPanel === "channels") {
        void loadChannels(host as unknown as IglooBotApp, false);
      }
      if (host.agentsPanel === "cron") {
        void loadCron(host);
      }
    }
  }
  if (host.tab === "nodes") {
    await loadNodes(host as unknown as IglooBotApp);
    await loadDevices(host as unknown as IglooBotApp);
    await loadConfig(host as unknown as IglooBotApp);
    await loadExecApprovals(host as unknown as IglooBotApp);
  }
  if (host.tab === "chat") {
    await refreshChat(host as unknown as Parameters<typeof refreshChat>[0]);
    scheduleChatScroll(
      host as unknown as Parameters<typeof scheduleChatScroll>[0],
      !host.chatHasAutoScrolled,
    );
    // Load console skills (skills with showInConsole=true in metadata)
    void loadConsoleSkills(host as unknown as IglooBotApp);
    // Auto-default to the default agent if no context is persisted
    // Load projects for the context picklist and default project fallback (non-blocking)
    void loadProjects(host as unknown as IgloobotState).then(() => {
      const ctxKey = host.settings.chatContextKey || "";
      const app = host as unknown as AppViewState;
      if (ctxKey.startsWith("project:") && app.client) {
        const projectId = ctxKey.slice("project:".length);
        app.chatProjectId = projectId;
        void (async () => {
          app.chatProjectSessionsLoading = true;
          try {
            const res = await app.client!.request<{
              ok: boolean;
              sessions?: Array<{
                id: string;
                type: string;
                status: string;
                channel: string | null;
                userName: string | null;
                projectName: string | null;
                metadata?: Record<string, unknown>;
                createdAt: string;
              }>;
            }>("igloobot.sessions.list", { projectId });
            if (res.ok && res.sessions) {
              app.chatProjectSessions = res.sessions as typeof app.chatProjectSessions;
            }
          } catch {
            /* ignore */
          }
          app.chatProjectSessionsLoading = false;
        })();
      } else if (!ctxKey) {
        // No project selected — fall back to the user's default project (named after username)
        const username = (host as unknown as { currentUser?: { username?: string } }).currentUser?.username;
        const igState = host as unknown as IgloobotState;
        const defaultProject = igState.igProjects?.find((p) => p.name === username);
        if (defaultProject) {
          const defaultKey = `project:${defaultProject.id}`;
          applySettings(host, {
            ...host.settings,
            chatContextKey: defaultKey,
          });
          // Trigger session load for the default project
          app.chatProjectId = defaultProject.id;
          void (async () => {
            app.chatProjectSessionsLoading = true;
            try {
              const res = await app.client!.request<{
                ok: boolean;
                sessions?: Array<{
                  id: string;
                  type: string;
                  status: string;
                  channel: string | null;
                  userName: string | null;
                  projectName: string | null;
                  metadata?: Record<string, unknown>;
                  createdAt: string;
                }>;
              }>("igloobot.sessions.list", { projectId: defaultProject.id });
              if (res.ok && res.sessions && res.sessions.length > 0) {
                app.chatProjectSessions = res.sessions as typeof app.chatProjectSessions;
                const firstKey =
                  (res.sessions[0].metadata?.sessionKey as string) ?? res.sessions[0].id;
                const switchFn = makeSwitchSession(app);
                switchFn(firstKey);
              } else {
                // No sessions exist — create a project-scoped session key
                const switchFn = makeSwitchSession(app);
                switchFn(`agent:main:project:${defaultProject.id}:main`);
              }
            } catch {
              /* ignore */
            }
            app.chatProjectSessionsLoading = false;
          })();
        }
      }
    });
  }
  if (host.tab === "security") {
    await loadConfig(host as unknown as IglooBotApp);
  }
  if (host.tab === "settings" || host.tab === "config") {
    await loadConfigSchema(host as unknown as IglooBotApp);
    await loadConfig(host as unknown as IglooBotApp);
  }
  if (host.tab === "debug") {
    await loadDebug(host as unknown as IglooBotApp);
    host.eventLog = host.eventLogBuffer;
  }
  if (host.tab === "logs") {
    host.logsAtBottom = true;
    await loadLogs(host as unknown as IglooBotApp, { reset: true });
    scheduleLogsScroll(host as unknown as Parameters<typeof scheduleLogsScroll>[0], true);
  }
  if (host.tab === "system-overview") {
    const { loadSystemOverview } = await import("./controllers/system-overview.ts");
    await loadSystemOverview(host as unknown as IglooBotApp);
  }
  if (host.tab === "agent-plans") {
    const { loadAgentPlans } = await import("./controllers/agent-plans.ts");
    await loadAgentPlans(host as unknown as IglooBotApp);
  }
  if (host.tab === "user-settings") {
    const { loadTabVisibility } = await import("./controllers/tab-visibility.ts");
    await loadTabVisibility(host as unknown as IglooBotApp);
    const { loadRoleSettings } = await import("./controllers/role-settings.ts");
    await loadRoleSettings(host as unknown as IglooBotApp);
  }
  if (host.tab === "inference-config") {
    const { loadInferenceConfig } = await import("./controllers/inference-config.ts");
    await loadInferenceConfig(host as unknown as IglooBotApp);
  }
  if (host.tab === "models") {
    const { loadModelsTab } = await import("./controllers/models.ts");
    await loadModelsTab(host as unknown as import("./controllers/models.ts").ModelsState);
  }
  if (
    host.tab === "projects" ||
    host.tab === "tasks" ||
    host.tab === "users" ||
    host.tab === "mcp-skills" ||
    host.tab === "avatar" ||
    host.tab === "company" ||
    host.tab === "email"
  ) {
    loadIgloobotTab(host as unknown as IgloobotState, host.tab);
  }
  // Avatar tab needs the model catalog for the LLM model picker
  if (host.tab === "avatar" && host.modelCatalog.length === 0) {
    const { loadModelsTab } = await import("./controllers/models.ts");
    await loadModelsTab(host as unknown as import("./controllers/models.ts").ModelsState);
  }
  if (host.tab === "n8n") {
    const { loadN8nWorkflows } = await import("./controllers/n8n.ts");
    await loadN8nWorkflows(host as unknown as import("./controllers/n8n.ts").N8nState);
    // Also load projects for the filter dropdown
    void loadProjects(host as unknown as IgloobotState);
  }
  if (host.tab === "pipelines") {
    const { loadPipelines } = await import("./controllers/pipelines.ts");
    await loadPipelines(host as unknown as import("./controllers/pipelines.ts").PipelinesState);
    void loadProjects(host as unknown as IgloobotState);
  }
  if (host.tab === "documents") {
    const { loadDocuments } = await import("./controllers/documents.ts");
    await loadDocuments(host as unknown as import("./controllers/documents.ts").DocumentsState);
    // Also load projects for the filter dropdown
    void loadProjects(host as unknown as IgloobotState);
  }
  if (host.tab === "computer") {
    const { loadComputerTab } = await import("./controllers/computer.ts");
    await loadComputerTab(host as unknown as import("./controllers/computer.ts").ComputerState);
  }
}

export function inferBasePath() {
  if (typeof window === "undefined") {
    return "";
  }
  const configured = window.__IGLOOBOT_CONTROL_UI_BASE_PATH__;
  if (typeof configured === "string" && configured.trim()) {
    return normalizeBasePath(configured);
  }
  return inferBasePathFromPathname(window.location.pathname);
}

export function syncThemeWithSettings(host: SettingsHost) {
  host.theme = host.settings.theme ?? "system";
  applyResolvedTheme(host, resolveTheme(host.theme));
}

export function applyResolvedTheme(host: SettingsHost, resolved: ResolvedTheme) {
  host.themeResolved = resolved;
  if (typeof document === "undefined") {
    return;
  }
  const root = document.documentElement;
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
}

export function attachThemeListener(host: SettingsHost) {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return;
  }
  host.themeMedia = window.matchMedia("(prefers-color-scheme: dark)");
  host.themeMediaHandler = (event) => {
    if (host.theme !== "system") {
      return;
    }
    applyResolvedTheme(host, event.matches ? "dark" : "light");
  };
  if (typeof host.themeMedia.addEventListener === "function") {
    host.themeMedia.addEventListener("change", host.themeMediaHandler);
    return;
  }
  const legacy = host.themeMedia as MediaQueryList & {
    addListener: (cb: (event: MediaQueryListEvent) => void) => void;
  };
  legacy.addListener(host.themeMediaHandler);
}

export function detachThemeListener(host: SettingsHost) {
  if (!host.themeMedia || !host.themeMediaHandler) {
    return;
  }
  if (typeof host.themeMedia.removeEventListener === "function") {
    host.themeMedia.removeEventListener("change", host.themeMediaHandler);
    return;
  }
  const legacy = host.themeMedia as MediaQueryList & {
    removeListener: (cb: (event: MediaQueryListEvent) => void) => void;
  };
  legacy.removeListener(host.themeMediaHandler);
  host.themeMedia = null;
  host.themeMediaHandler = null;
}

export function syncTabWithLocation(host: SettingsHost, replace: boolean) {
  if (typeof window === "undefined") {
    return;
  }
  const resolved = tabFromPath(window.location.pathname, host.basePath) ?? "chat";
  setTabFromRoute(host, resolved);
  syncUrlWithTab(host, resolved, replace);
}

export function onPopState(host: SettingsHost) {
  if (typeof window === "undefined") {
    return;
  }
  const resolved = tabFromPath(window.location.pathname, host.basePath);
  if (!resolved) {
    return;
  }

  const url = new URL(window.location.href);
  const session = url.searchParams.get("session")?.trim();
  if (session) {
    host.sessionKey = session;
    applySettings(host, {
      ...host.settings,
      sessionKey: session,
      lastActiveSessionKey: session,
    });
  }

  setTabFromRoute(host, resolved);
}

export function setTabFromRoute(host: SettingsHost, next: Tab) {
  if (host.tab !== next) {
    host.tab = next;
  }
  if (next === "chat") {
    host.chatHasAutoScrolled = false;
  }
  if (next === "logs") {
    startLogsPolling(host as unknown as Parameters<typeof startLogsPolling>[0]);
  } else {
    stopLogsPolling(host as unknown as Parameters<typeof stopLogsPolling>[0]);
  }
  if (next === "debug") {
    startDebugPolling(host as unknown as Parameters<typeof startDebugPolling>[0]);
  } else {
    stopDebugPolling(host as unknown as Parameters<typeof stopDebugPolling>[0]);
  }
  if (host.connected) {
    void refreshActiveTab(host);
  }
}

export function syncUrlWithTab(host: SettingsHost, tab: Tab, replace: boolean) {
  if (typeof window === "undefined") {
    return;
  }
  const targetPath = normalizePath(pathForTab(tab, host.basePath));
  const currentPath = normalizePath(window.location.pathname);
  const url = new URL(window.location.href);

  if (tab === "chat" && host.sessionKey) {
    url.searchParams.set("session", host.sessionKey);
  } else {
    url.searchParams.delete("session");
  }

  if (currentPath !== targetPath) {
    url.pathname = targetPath;
  }

  if (replace) {
    window.history.replaceState({}, "", url.toString());
  } else {
    window.history.pushState({}, "", url.toString());
  }
}

export function syncUrlWithSessionKey(host: SettingsHost, sessionKey: string, replace: boolean) {
  if (typeof window === "undefined") {
    return;
  }
  const url = new URL(window.location.href);
  url.searchParams.set("session", sessionKey);
  if (replace) {
    window.history.replaceState({}, "", url.toString());
  } else {
    window.history.pushState({}, "", url.toString());
  }
}

export async function loadOverview(host: SettingsHost) {
  await Promise.all([
    loadChannels(host as unknown as IglooBotApp, false),
    loadPresence(host as unknown as IglooBotApp),
    loadSessions(host as unknown as IglooBotApp),
    loadCronStatus(host as unknown as IglooBotApp),
    loadDebug(host as unknown as IglooBotApp),
    loadIgloobotStats(host as unknown as IglooBotApp),
  ]);
}

export async function loadChannelsTab(host: SettingsHost) {
  await Promise.all([
    loadChannels(host as unknown as IglooBotApp, true),
    loadConfigSchema(host as unknown as IglooBotApp),
    loadConfig(host as unknown as IglooBotApp),
  ]);
}

export async function loadCron(host: SettingsHost) {
  await Promise.all([
    loadChannels(host as unknown as IglooBotApp, false),
    loadCronStatus(host as unknown as IglooBotApp),
    loadCronJobs(host as unknown as IglooBotApp),
  ]);
}
